import React, { useEffect, useState } from "react"
import { DollarSign, TrendingDown, Target, Calendar } from "lucide-react"
import { apiService } from "lib/apiService"
import { useHistory } from "react-router"

const iconMap = {
  income: DollarSign,
  expenses: TrendingDown,
  savings: Target,
  months: Calendar,
}

function animateValue(start, end, duration, onUpdate) {
  const range = end - start
  let startTime = performance.now()

  function step(timestamp) {
    const progress = Math.min((timestamp - startTime) / duration, 1)
    const value = Math.floor(start + range * progress)
    onUpdate(value)
    if (progress < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

export default function UniqueStatCards({ selectedGlobalYear, currency }) {
  const history = useHistory()

  // Redirect if not logged in
  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      history.push("/login")
    }
  }, [history])

  const storedUserData = localStorage.getItem("userData")
  let groupId = ""

  if (storedUserData) {
    const userData = JSON.parse(storedUserData)
    groupId = userData.groupId
  }

  const [metrics, setMetrics] = useState([
    { key: "income", title: "Total Credits", value: 0, final: 0, change: "+0%", gradient: "linear-gradient(135deg, #93c5fd, #1e3a8a)" },
    { key: "expenses", title: "Total Debits", value: 0, final: 0, change: "+0%", gradient: "linear-gradient(135deg, #fca5a5, #b91c1c)" },
    { key: "savings", title: "Net Balance", value: 0, final: 0, change: "", gradient: "linear-gradient(135deg, #86efac, #166534)" },
    { key: "months", title: "Active Months", value: 0, final: 0, change: "+0%", gradient: "linear-gradient(135deg, #d8b4fe, #6b21a8)" },
  ])

  const getChange = (curr, prev) => {
    if (prev === 0) return curr === 0 ? "0%" : "+100%"
    const diff = ((curr - prev) / prev) * 100
    return `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}%`
  }

  useEffect(() => {
    const fetchStats = async () => {
      if (!groupId || !selectedGlobalYear) return

      const res = await fetch(
        `https://expense-chart-w33t.vercel.app/api/stats?groupId=${groupId}&year=${selectedGlobalYear}`
      )
      const data = await res.json()

      if (!data || !data.curr) return

      const curr = data.curr
      const prev = data.prev
      const netBalance = Number(data.netBalance)

      const updated = [
        {
          ...metrics[0],
          final: curr.totalCredit,
          change: getChange(curr.totalCredit, prev.totalCredit),
        },
        {
          ...metrics[1],
          final: curr.totalDebit,
          change: getChange(curr.totalDebit, prev.totalDebit),
        },
        {
          ...metrics[2],
          final: netBalance,
          change: "", // ❌ NO CHANGE FOR NET BALANCE
        },
        {
          ...metrics[3],
          final: curr.activeMonths,
          change: getChange(curr.activeMonths, prev.activeMonths),
        },
      ]

      updated.forEach((metric, i) => {
        animateValue(metrics[i].value, metric.final, 1000, (val) => {
          setMetrics((prev) => {
            const newMetrics = [...prev]
            newMetrics[i] = { ...metric, value: val }
            return newMetrics
          })
        })
      })
    }

    fetchStats()
  }, [groupId, selectedGlobalYear])

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
      {metrics.map(({ key, title, value, change, gradient }) => {
        const Icon = iconMap[key]
        const displayVal =
          key === "months" ? `${value} / 12` : `${currency}${value.toLocaleString()}`

        return (
          <div
            key={key}
            style={{
              padding: "20px",
              borderRadius: "16px",
              background: gradient,
              color: "white",
              boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", top: "10px", right: "10px", opacity: 0.2, transform: "rotate(12deg)" }}>
              <Icon size={70} />
            </div>

            <div style={{ position: "relative", zIndex: 10 }}>
              <div style={{ fontSize: "12px", fontWeight: "600", textTransform: "uppercase" }}>{title}</div>

              <div style={{ fontSize: "30px", fontWeight: "bold", marginTop: "10px" }}>
                {displayVal}
              </div>

              {change && (
                <div style={{ fontSize: "11px", marginTop: "5px", opacity: 0.8 }}>{change}</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
